import type { ArticleCategoryResponse, ArticleResponse } from '@shared/api-utils';
import { createDebug } from '@shared/debug';
import { ROUTES } from '@shared/utils';

const debug = createDebug({ isEnabled: false });

type MagazineArticle = ArticleResponse & {
  category: ArticleCategoryResponse | undefined;
};

function isMagazineCategory(category: ArticleCategoryResponse): boolean {
  const canonicalUrl = category.seoInfo.canonicalUrl;
  return canonicalUrl === ROUTES.MAGAZINE.HOME || canonicalUrl.startsWith(`${ROUTES.MAGAZINE.HOME}/`);
}

/**
 * Finds a magazine category whose CMS URL slug matches the route param
 * (e.g. `lotto-news`).
 */
function findMagazineCategoryBySlug(categories: readonly ArticleCategoryResponse[], categorySlug: string): ArticleCategoryResponse | undefined {
  if (!categorySlug) {
    return undefined;
  }

  return categories.find(category => isMagazineCategory(category) && category.slug === categorySlug);
}

/**
 * Finds a category by CMS id (e.g. article `categories[]` values and the articles `category` filter).
 */
function findMagazineCategory(categories: readonly ArticleCategoryResponse[], categoryId: string): ArticleCategoryResponse | undefined {
  if (!categoryId) {
    return undefined;
  }

  return categories.find(category => category.id === categoryId);
}

/**
 * Resolves an article's primary API category id to the matching CMS category.
 * Articles with no categories (empty, null, or omitted), or with an id missing from the categories list,
 * return `undefined` so enrichment can degrade gracefully.
 */
function resolveMagazineArticleCategory(
  categories: readonly ArticleCategoryResponse[],
  articleCategoryIds: readonly string[] | null | undefined
): ArticleCategoryResponse | undefined {
  const categoryId = articleCategoryIds?.[0];
  if (!categoryId) {
    return undefined;
  }

  const matchedCategory = findMagazineCategory(categories, categoryId);
  if (!matchedCategory) {
    debug.forceError(`Magazine category "${categoryId}" was not found in the categories API response`);
    return undefined;
  }

  return matchedCategory;
}

/**
 * Joins the primary article category from the CMS categories list.
 * Articles with no categories, or with an unknown id, set `category` to `undefined`.
 */
function enrichMagazineArticle(article: ArticleResponse, categories: readonly ArticleCategoryResponse[]): MagazineArticle {
  return {
    ...article,
    category: resolveMagazineArticleCategory(categories, article.categories),
  };
}

function enrichMagazineArticles(articles: readonly ArticleResponse[], categories: readonly ArticleCategoryResponse[]): MagazineArticle[] {
  return articles.map(article => enrichMagazineArticle(article, categories));
}

export {
  enrichMagazineArticle,
  enrichMagazineArticles,
  findMagazineCategory,
  findMagazineCategoryBySlug,
  isMagazineCategory,
  resolveMagazineArticleCategory,
  type MagazineArticle,
};
